import { useMemo } from "react";

import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";

import { initUseQuery } from "simplydo/core/query";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Divider } from "simplydo/react/ui/divider";
import { H3, Subheader } from "simplydo/react/ui/header";
import { Label } from "simplydo/react/ui/label";
import { Menu, MenuItem } from "simplydo/react/ui/menu";
import {
  Message,
  MessageActions,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";
import { Tab, TabPane } from "simplydo/react/ui/tab";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import type { ChallengeDetail, IdeaTemplateAssessmentRound } from "../../types";
import YourAssessmentRound from "./YourAssessmentRound";

type IChallengeYourAssessments = {
  challenge: ChallengeDetail;
  demoingTour?: string;
};

function ChallengeYourAssessments({
  challenge,
  demoingTour,
}: IChallengeYourAssessments) {
  const user = useAppSelector((state) => state.user);

  const assessmentRounds =
    challenge.ownerIdeaTemplate?.assessments?.rounds || [];

  const assignedAssessmentQuery = useQuery(
    initUseQuery(api.challenges.getAssignedAssessments, [challenge?._id], {
      isEnabled: !!user?._id,
    }),
  );
  const assignedAssessmentIdeas = assignedAssessmentQuery.data?.ideas;

  const [incompleteAssignments, allAssignments] = useMemo(() => {
    const incompleteAssessmentsPerRound: Record<string, number> = {};
    const assessmentsPerRound: Record<string, number> = {};
    assignedAssessmentIdeas?.forEach((idea) => {
      idea.assessments?.forEach((assessment) => {
        const roundId = assessment.assessmentRoundId || "legacy";

        if (assessment.user !== user?._id) return;
        if (!assessmentsPerRound[roundId]) {
          assessmentsPerRound[roundId] = 0;
        }
        assessmentsPerRound[roundId] += 1;
        if (assessment.isSubmitted) return;
        if (!incompleteAssessmentsPerRound[roundId]) {
          incompleteAssessmentsPerRound[roundId] = 0;
        }
        incompleteAssessmentsPerRound[roundId] += 1;
      });
    });
    return [incompleteAssessmentsPerRound, assessmentsPerRound];
  }, [user?._id, assignedAssessmentIdeas]);

  const { t } = useTranslation();

  return (
    <>
      {!user ? (
        <Message>
          <MessageHeader>Please log in to view this page</MessageHeader>
          <MessageContent>
            Register or login to your {util.appName()} account to view your
            assessments for this {t("generic.challenge")}.
          </MessageContent>
          <MessageActions>
            <ButtonLink
              color="primary"
              to={`/login?then=/challenges/${challenge?._id}/assessments`}
            >
              Login or register
            </ButtonLink>
          </MessageActions>
        </Message>
      ) : (
        <Tab
          panes={assessmentRounds
            .filter(
              (round: IdeaTemplateAssessmentRound) =>
                allAssignments[round.id || "legacy"] &&
                allAssignments[round.id || "legacy"]! > 0,
            )
            .map((round: IdeaTemplateAssessmentRound) => ({
              menuItem: (
                <MenuItem key={round.id}>
                  {round.name}
                  {incompleteAssignments[round.id] ? (
                    <Label circular color="red" size="small">
                      {incompleteAssignments[round.id]}
                    </Label>
                  ) : null}
                </MenuItem>
              ),
              key: round.id,
              render: () => (
                <TabPane key={round.id}>
                  <YourAssessmentRound
                    assessmentRound={round}
                    challenge={challenge}
                    demoingTour={demoingTour}
                  />
                </TabPane>
              ),
            }))}
        />
      )}
    </>
  );
}

export default ChallengeYourAssessments;
